"use client";

import Link from "next/link";
import { ArrowRight } from "lucide-react";
import type { PublicTherapistSummary } from "@/actions/getTherapists";
import { cn } from "@/lib/utils";
import type {
  TestSlug,
  TherapistCTABlock,
} from "@/data/tests/types";

interface SoftTherapistCTAProps {
  category: string;
  block: TherapistCTABlock;
  therapists: PublicTherapistSummary[];
  testSlug: TestSlug;
  /** UTM medium for tracking */
  utmMedium: string;
  /** Whether to style as urgent (severe-style red CTA). Defaults false. */
  urgent?: boolean;
  /** Override primary accent for the CTA button when not urgent */
  accent?: string;
}

export default function SoftTherapistCTA({
  block,
  therapists,
  utmMedium,
  urgent = false,
  accent = "#2C969B",
}: SoftTherapistCTAProps) {
  const slice = therapists.slice(0, 3);
  const ctaUrl = `/gaseste-terapeut?energy=${block.energyParam}&mt_entry=internal&mt_medium=${utmMedium}&utm_campaign=${utmMedium}_result`;

  return (
    <section
      className={cn(
        "rounded-2xl p-6 sm:p-8 border",
        urgent
          ? "bg-[#DC7050]/8 border-[#DC7050]/25"
          : "bg-white border-[#264653]/10"
      )}
    >
      <p
        className="text-[11px] font-semibold uppercase tracking-widest mb-2"
        style={{ color: urgent ? "#B85037" : accent }}
      >
        Recomandare pentru tine
      </p>
      <h3 className="text-2xl sm:text-3xl font-semibold text-[#264653] leading-tight mb-3">
        {block.headline}
      </h3>
      <p className="text-[#264653]/75 text-[15px] sm:text-base leading-relaxed mb-6 max-w-2xl">
        {block.subline}
      </p>

      {slice.length > 0 && (
        <div className="grid sm:grid-cols-3 gap-3 mb-6">
          {slice.map((t) => {
            const fullName = [t.firstName, t.lastName]
              .filter(Boolean)
              .join(" ");
            return (
              <Link
                key={t.id}
                href={`/terapeuti/${t.slug ?? t.id}?mt_entry=internal&mt_medium=${utmMedium}&utm_campaign=${utmMedium}_match`}
                className="group bg-white rounded-xl border border-[#264653]/8 p-4 hover:border-[#2C969B]/40 hover:shadow-sm transition-all"
              >
                <div className="flex items-start gap-3">
                  {t.avatarUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={t.avatarUrl}
                      alt={fullName || "Terapeut Mindtale"}
                      width={48}
                      height={48}
                      loading="lazy"
                      className="rounded-full object-cover size-12 shrink-0"
                    />
                  ) : (
                    <div
                      className="size-12 rounded-full shrink-0 flex items-center justify-center font-semibold"
                      style={{
                        background: `${accent}1A`,
                        color: accent,
                      }}
                    >
                      {fullName.charAt(0)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-[#264653] truncate">
                      {fullName || "Terapeut Mindtale"}
                    </p>
                    {t.specializations && t.specializations.length > 0 && (
                      <p className="text-xs text-[#264653]/60 leading-snug mt-0.5 line-clamp-2">
                        {t.specializations.slice(0, 3).join(" · ")}
                      </p>
                    )}
                  </div>
                </div>
                <p
                  className="text-xs font-medium mt-3 inline-flex items-center gap-1 group-hover:gap-1.5 transition-all"
                  style={{ color: accent }}
                >
                  Vezi profilul
                  <ArrowRight className="size-3" />
                </p>
              </Link>
            );
          })}
        </div>
      )}

      <Link
        href={ctaUrl}
        className={cn(
          "inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-[15px] transition-all text-white",
          "hover:gap-3 hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        )}
        style={{ background: urgent ? "#DC7050" : accent }}
      >
        {block.ctaLabel}
        <ArrowRight className="size-4" />
      </Link>
    </section>
  );
}
